import { formatCurrency } from "../../../../utils/currency";

function safeObject(value, fallback = {}) {
  if (!value) return fallback;

  if (typeof value === "object" && !Array.isArray(value)) return value;

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : fallback;
    } catch {
      return fallback;
    }
  }

  return fallback;
}

export default function HistoryWorkspace({
  versions = [],
  onRestoreVersion,
  selectedCurrency = "DKK",
  formatDateTime,
}) {
  if (!versions.length) {
    return (
      <div className="h-full bg-[#f3f2f1] p-4">
        <div className="rounded-sm border border-[#edebe9] bg-white px-4 py-3 text-sm text-[#605e5c] shadow-sm">
          No saved versions yet.
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-[#f3f2f1] p-4 text-[#201f1e]">
      <section className="overflow-hidden rounded-sm border border-[#edebe9] bg-white shadow-sm">
        <div className="border-b border-[#edebe9] bg-[#faf9f8] px-4 py-3">
          <h2 className="text-sm font-semibold text-[#201f1e]">
            Version history
          </h2>
          <p className="mt-0.5 text-xs text-[#605e5c]">
            Restore a previous saved quote version.
          </p>
        </div>

        <table className="min-w-full border-collapse text-sm">
          <thead className="bg-[#f3f2f1]">
            <tr className="border-b border-[#edebe9]">
              <HeaderCell>Version</HeaderCell>
              <HeaderCell>Saved at</HeaderCell>
              <HeaderCell>Status</HeaderCell>
              <HeaderCell align="right">Total</HeaderCell>
              <HeaderCell align="right">Action</HeaderCell>
            </tr>
          </thead>

          <tbody>
            {versions.map((version, index) => {
              const isLatest = index === 0;
              const estimate = safeObject(version.estimate_json, {});
              const total = Number(estimate?.summary?.total || 0);

              return (
                <tr
                  key={version.id || index}
                  className="border-b border-[#edebe9] bg-white hover:bg-[#f3f2f1]"
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2 font-medium text-[#201f1e]">
                      Version {versions.length - index}

                      {isLatest && (
                        <span className="rounded-sm border border-[#c7e0c0] bg-[#dff6dd] px-1.5 py-0.5 text-[10px] font-medium uppercase text-[#107c10]">
                          Current
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="px-3 py-2 text-[#605e5c]">
                    {formatDateTime
                      ? formatDateTime(version.created_at)
                      : version.created_at || "—"}
                  </td>

                  <td className="px-3 py-2 text-[#605e5c]">
                    {version.status || "draft"}
                  </td>

                  <td className="px-3 py-2 text-right font-medium tabular-nums text-[#201f1e]">
                    {formatCurrency(total, selectedCurrency)}
                  </td>

                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => onRestoreVersion?.(version)}
                      disabled={isLatest}
                      className="rounded-sm border border-[#edebe9] bg-white px-3 py-1.5 text-xs font-medium text-[#323130] hover:bg-[#f3f2f1] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isLatest ? "Current" : "Restore"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function HeaderCell({ children, align = "left" }) {
  return (
    <th
      className={[
        "border-r border-[#edebe9] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[#605e5c] last:border-r-0",
        align === "right" ? "text-right" : "text-left",
      ].join(" ")}
    >
      {children}
    </th>
  );
}